/**
 * KonvaFogLayerContainer - Container Component
 *
 * Container component responsible for:
 * - Zustand store connection (useStore, useActions)
 * - Reading fogSettings from the store
 * - Providing onSaveFog callback that persists fog data to the store
 * - Delegating presentation to the pure KonvaFogLayer component
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { useStore, useActions } from '../../hooks/useStore';
import { useStableCallback } from '../../hooks/hookUtils';
import KonvaFogLayer from './KonvaFogLayer';

interface KonvaFogLayerContainerProps {
  imageRect: { x: number; y: number; width: number; height: number; };
  containerSize: { width: number; height: number; } | null;
}

const KonvaFogLayerContainer: React.FC<KonvaFogLayerContainerProps> = ({ imageRect, containerSize }) => {
  const fogSettings = useStore(state => state.settings?.fogOfWar);
  const actions = useActions();

  // useStableCallback ensures the save callback always accesses the latest
  // fogSettings without being recreated, which is critical for the debounced
  // save logic inside the presenter.
  const handleSaveFog = useStableCallback((dataUrl: string) => {
    actions.updateSettings({ fogOfWar: { ...fogSettings, data: dataUrl } });
  });

  return (
    <KonvaFogLayer
      imageRect={imageRect}
      containerSize={containerSize}
      fogSettings={fogSettings}
      onSaveFog={handleSaveFog}
    />
  );
};

export default KonvaFogLayerContainer;
